import type { AttentionReason, ColumnRole, GeocodeMode, IngestSource } from "@/lib/ingest";

/**
 * Plain-language display strings for the ingest surfaces. UI copy only —
 * generic and brand-free (everything brand-facing resolves from the
 * white-label theme config, never from literals here).
 */

export const ROLE_LABELS: Record<ColumnRole, string> = {
  address_full: "Full address",
  street: "Street",
  city: "City",
  state: "State",
  zip: "ZIP",
  country: "Country",
  lat: "Latitude",
  lng: "Longitude",
  label: "Label",
  ignore: "Ignored",
};

/** Short cause shown as the attention-row heading (what is wrong). */
export const REASON_LABELS: Record<AttentionReason, string> = {
  missing_address: "Add a location",
  missing_locality: "Add a city, street, or ZIP",
  bad_coords: "Fix latitude and longitude coordinates.",
  empty_row: "Blank row",
  parse_error: "Summary row",
  out_of_range: "Coordinates out of range",
};

/**
 * Plain-language "how to fix it" line shown under the cause, so a new user can
 * self-serve instead of guessing. Kept generic/brand-free and phrased as the
 * concrete edit to make (or the escape hatch: exclude the row).
 */
export const REASON_HINTS: Record<AttentionReason, string> = {
  missing_address: "Enter a street, city, or ZIP.",
  missing_locality: "Enter a city, street, or ZIP.",
  bad_coords: "Enter a valid latitude and longitude.",
  out_of_range: "Check the latitude and longitude values.",
  empty_row: "A blank line — nothing to place. Safe to ignore.",
  parse_error: "A Total/summary row, not a place. Safe to ignore.",
};

export const MODE_SENTENCE: Record<GeocodeMode, string> = {
  address: "Reading each row as a street address.",
  coordinate: "Reading each row as latitude / longitude coordinates.",
};

export function columnDisplayName(header: string, index: number): string {
  const trimmed = header.trim();
  return trimmed !== "" ? trimmed : `Column ${index + 1}`;
}

export function sourceDisplayName(source: IngestSource): string {
  if (source.kind === "paste") return "Pasted rows";
  if (source.kind === "sheets_url") return "Google Sheet";
  return source.filename ?? "Spreadsheet";
}
